import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { T, Th, Tr, ActionBtn, Modal, TableCard, TableBar, SearchInput, Toast, PrimaryBtn, statusBadge, visibilityBadge } from './theme';
import { useRefresh } from './RefreshContext';
import API_BASE_URL from '../config/api';

const MyArticlesSection = () => {
  const { role } = useAuth();
  const { refresh, triggerRefresh } = useRefresh();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState('');
  const [viewArticle, setViewArticle] = useState(null);
  const [viewReview, setViewReview] = useState(null);
  const [editArticle, setEditArticle] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', content: '' });

  const userEmail = localStorage.getItem('vrvEmail') || '';
  const roles = (role || '').split(',').map(s => s.trim().toUpperCase());
  const isAdmin = roles.includes('ADMIN');
  const canDirectPublish = isAdmin || (roles.includes('CONTENT-CREATOR') && roles.includes('CONTENT-APPROVER'));

  const fetchMyArticles = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/vrv-articlecontent-getAll`);
      const data = await res.json();
      const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
      setArticles((parsed.data || []).filter(a => (a.createdBy || '').toLowerCase() === userEmail.toLowerCase()).sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)));
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchMyArticles(); }, [userEmail, refresh]); // eslint-disable-line

  const showMsg = (message, type) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: '' }), 3500);
  };

  const filtered = articles.filter(a => !search || Object.values(a).some(v => String(v).toLowerCase().includes(search.toLowerCase())));
  const toggleSelect = id => setSelectedIds(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleAll = () => setSelectedIds(selectedIds.length === filtered.length ? [] : filtered.map(a => a.content_id));

  const handleBulkAction = async (action) => {
    if (!selectedIds.length) { showMsg('Select at least one item', 'error'); return; }
    if ((action === 'PUBLIC' || action === 'PRIVATE') && filtered.some(a => selectedIds.includes(a.content_id) && a.status !== 'APPROVED')) {
      showMsg('Cannot change visibility for non-approved content', 'error'); return;
    }
    try {
      const token = localStorage.getItem('vrvToken');
      let ok = true;
      for (const content_id of selectedIds) {
        const res = await fetch(`${API_BASE_URL}/vrv-articlecontent-action`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ content_id, action }) });
        if (!res.ok) ok = false;
      }
      showMsg(ok ? `${action} applied to ${selectedIds.length} item(s)` : 'Some actions failed', ok ? 'success' : 'error');
      setSelectedIds([]); fetchMyArticles(); triggerRefresh();
    } catch { showMsg('Error connecting', 'error'); }
  };

  const handleEdit = (a) => { setEditArticle(a); setEditForm({ title: a.title || '', content: a.content || '' }); };
  const hasChanges = () => editArticle && (editForm.title !== (editArticle.title || '') || editForm.content !== (editArticle.content || ''));

  const handleEditSave = async () => {
    if (!editForm.title || !editForm.content) { showMsg('Title and content required', 'error'); return; }
    if (!hasChanges()) { showMsg('No changes to save', 'error'); return; }
    try {
      const token = localStorage.getItem('vrvToken');
      const res = await fetch(`${API_BASE_URL}/vrv-articlecontent-action`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ content_id: editArticle.content_id, action: 'UPDATE', ...editForm, status: canDirectPublish ? 'APPROVED' : 'PENDING', visibility: canDirectPublish ? 'PUBLIC' : 'PRIVATE' }) });
      if (res.ok) { showMsg(canDirectPublish ? 'Article updated!' : 'Sent for re-approval!', 'success'); setEditArticle(null); fetchMyArticles(); triggerRefresh(); }
      else showMsg('Update failed', 'error');
    } catch { showMsg('Error connecting', 'error'); }
  };

  if (loading) return <div className="text-center py-16"><p className="text-sm" style={{ color: T.muted }}>Loading…</p></div>;
  if (!articles.length) return (
    <div className="vrv-card text-center py-16">
      <div className="text-4xl mb-3">📖</div>
      <p className="font-semibold" style={{ color: T.muted }}>No articles posted by you yet.</p>
    </div>
  );

  return (
    <div>
      <div className="mb-4"><SearchInput value={search} onChange={e => setSearch(e.target.value)} /></div>

      {/* Desktop Table */}
      <div className="desktop-table">
        <TableCard>
          <TableBar className="table-bar-wrap">
            <p className="text-xs font-bold" style={{ color: T.muted }}>{selectedIds.length > 0 ? `${selectedIds.length} selected` : `${filtered.length} articles`}</p>
            <div className="flex flex-wrap gap-2">
              {[['Public', 'gold'], ['Private', 'muted'], ['Delete', 'red']].map(([label, color]) => (
                <ActionBtn key={label} onClick={() => handleBulkAction(label.toUpperCase())} disabled={!selectedIds.length} color={color}>{label}</ActionBtn>
              ))}
            </div>
          </TableBar>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr>
                <th className="px-3 py-3 w-8" style={{ background: T.stripe, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                  <input type="checkbox" checked={selectedIds.length === filtered.length && filtered.length > 0} onChange={toggleAll} />
                </th>
                <Th>Title</Th><Th>Date</Th><Th>Status</Th><Th>Visibility</Th><Th center>View</Th><Th center>Edit</Th><Th center>Review</Th>
              </tr></thead>
              <tbody>
                {filtered.map((a, i) => (
                  <Tr key={a.content_id || i} selected={selectedIds.includes(a.content_id)}>
                    <td className="px-3 py-2.5"><input type="checkbox" checked={selectedIds.includes(a.content_id)} onChange={() => toggleSelect(a.content_id)} /></td>
                    <td className="px-3 py-2.5 font-medium" style={{ color: T.ink }}>{a.title || '—'}</td>
                    <td className="px-3 py-2.5 whitespace-nowrap text-xs" style={{ color: T.muted }}>{a.createdAt ? new Date(a.createdAt).toLocaleDateString() : '—'}</td>
                    <td className="px-3 py-2.5"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest" style={statusBadge(a.status)}>{a.status || '—'}</span></td>
                    <td className="px-3 py-2.5"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest" style={visibilityBadge(a.visibility)}>{a.visibility}</span></td>
                    <td className="px-3 py-2.5 text-center"><ActionBtn onClick={() => setViewArticle(a)} color="muted" small>View</ActionBtn></td>
                    <td className="px-3 py-2.5 text-center"><ActionBtn onClick={() => handleEdit(a)} color="gold" small>Edit</ActionBtn></td>
                    <td className="px-3 py-2.5 text-center">{a.reviewedBy ? <ActionBtn onClick={() => setViewReview(a)} color="muted" small>Review</ActionBtn> : <span style={{ color: T.muted }}>—</span>}</td>
                  </Tr>
                ))}
              </tbody>
            </table>
          </div>
        </TableCard>
      </div>

      {/* Mobile Cards */}
      <div className="mobile-cards">
        <div className="flex flex-wrap gap-2 mb-3">
          {[['Public', 'gold'], ['Private', 'muted'], ['Delete', 'red']].map(([label, color]) => (
            <ActionBtn key={label} onClick={() => handleBulkAction(label.toUpperCase())} disabled={!selectedIds.length} color={color}>{label}</ActionBtn>
          ))}
        </div>
        {filtered.map((a, i) => (
          <div key={a.content_id || i} className="vrv-mobile-card" style={{ border: selectedIds.includes(a.content_id) ? `1.5px solid ${T.gold}` : undefined }}>
            <div className="flex items-start gap-3 mb-2">
              <input type="checkbox" checked={selectedIds.includes(a.content_id)} onChange={() => toggleSelect(a.content_id)} className="mt-1" />
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate" style={{ color: T.navy }}>{a.title || '—'}</p>
                <p className="text-xs" style={{ color: T.muted }}>{a.createdAt ? new Date(a.createdAt).toLocaleDateString() : '—'}</p>
              </div>
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest flex-shrink-0" style={statusBadge(a.status)}>{a.status || '—'}</span>
            </div>
            <div className="text-xs mb-3">
              <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold uppercase" style={visibilityBadge(a.visibility)}>{a.visibility}</span>
            </div>
            <div className="flex gap-2 pt-2" style={{ borderTop: '1px solid rgba(0,0,0,0.06)' }}>
              <ActionBtn onClick={() => setViewArticle(a)} color="muted" small>View</ActionBtn>
              <ActionBtn onClick={() => handleEdit(a)} color="gold" small>Edit</ActionBtn>
              {a.reviewedBy && <ActionBtn onClick={() => setViewReview(a)} color="muted" small>Review</ActionBtn>}
            </div>
          </div>
        ))}
      </div>

      {viewArticle && (
        <Modal onClose={() => setViewArticle(null)} title={viewArticle.title} subtitle="Article Preview" wide>
          <p className="text-xs mb-4" style={{ color: T.muted }}>{viewArticle.createdAt ? new Date(viewArticle.createdAt).toLocaleDateString() : ''}</p>
          <p className="text-sm leading-relaxed whitespace-pre-wrap mb-6 pt-4" style={{ color: T.ink, borderTop: '1px solid rgba(0,0,0,0.06)' }}>{viewArticle.content}</p>
          <PrimaryBtn onClick={() => setViewArticle(null)}>Close</PrimaryBtn>
        </Modal>
      )}

      {editArticle && (
        <Modal onClose={() => setEditArticle(null)} title="Edit Article" subtitle="My Content" wide>
          <div className="space-y-4">
            <div>
              <label className="vrv-label">Title *</label>
              <input type="text" value={editForm.title} onChange={e => setEditForm({ ...editForm, title: e.target.value })} className="vrv-input" />
            </div>
            <div>
              <label className="vrv-label">Content *</label>
              <textarea value={editForm.content} onChange={e => setEditForm({ ...editForm, content: e.target.value })} rows="8" className="vrv-input resize-none" />
            </div>
          </div>
          <div className="flex gap-3 mt-6">
            <ActionBtn onClick={() => setEditArticle(null)} color="muted">Cancel</ActionBtn>
            <button onClick={handleEditSave} disabled={!hasChanges()} className="btn-navy" style={{ opacity: hasChanges() ? 1 : 0.45 }}>
              {canDirectPublish ? 'Update & Publish →' : 'Update & Send for Approval →'}
            </button>
          </div>
        </Modal>
      )}

      {viewReview && (
        <Modal onClose={() => setViewReview(null)} title="Review Details" subtitle="Review Info">
          {[['Reviewed By', viewReview.reviewedBy], ['Reviewed At', viewReview.reviewedAt ? new Date(viewReview.reviewedAt).toLocaleString() : '—'], ['Comments', viewReview.reviewComments || 'No comments']].map(([label, val]) => (
            <div key={label} className="mb-4">
              <label className="vrv-label">{label}</label>
              <p className="text-sm px-3 py-2.5 rounded-xl whitespace-pre-wrap" style={{ background: '#F8F5EE', color: T.ink, border: '1px solid rgba(0,0,0,0.07)' }}>{val}</p>
            </div>
          ))}
          <div className="mt-4"><PrimaryBtn onClick={() => setViewReview(null)}>Close</PrimaryBtn></div>
        </Modal>
      )}

      <Toast toast={toast} onClose={() => setToast({ show: false, message: '', type: '' })} />
    </div>
  );
};

export default MyArticlesSection;
